/* ski-finder.js — decision-tree ski finder.
   Tree, skis, profiles, length rules and copy all live in
   assets/ski-finder-config.json so next season's catalogue change doesn't
   need a code change. This file only walks that data and talks to the
   storefront's existing /products/{handle}.js endpoint for live
   price/image/variant data. Vanilla JS, no dependencies. */
(function () {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (root.charAt(root.length - 1) !== '/') root += '/';

  var configPromise = null;
  var productCache = {};

  function loadConfig(url) {
    if (!configPromise) {
      configPromise = fetch(url).then(function (res) {
        if (!res.ok) throw new Error('ski-finder: could not load config');
        return res.json();
      });
    }
    return configPromise;
  }

  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(root + 'products/' + handle + '.js')
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    return currency ? amount + ' ' + currency : amount;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function currentLang() {
    // Shopify.locale can be a full tag ("fr-FR", "de-CH"), but the copy
    // table and every localized tree/result string are keyed by the bare
    // language ("fr", "de") — only the part before the dash matters.
    var raw = (window.Shopify && window.Shopify.locale) || 'en';
    return raw.split('-')[0].toLowerCase();
  }

  // Every question/answer/result string in the config is either a plain
  // English string (kept for anything not yet translated) or a
  // {en, fr, de, ...} object — this picks the right one for the current
  // language, falling back to English.
  function localize(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    var lang = currentLang();
    return value[lang] != null ? value[lang] : value.en;
  }

  /* Recommended length ------------------------------------------------------
     Every model's rule is a window (offsetMinCm/offsetMaxCm) plus which edge
     of that window to prefer:
       - preferEdge "max" (Revolution, Alp Tracks, Sequence, Fly 92/102):
         prefer the length closest to the window's upper edge, reaching
         down toward the lower edge only if nothing fits at/below the upper
         edge. Critically, the upper edge is a hard ceiling here — even the
         fallback never proposes a length above it, since for these models
         that edge is at or below the skier's actual height and a ski
         longer than that is never offered, no matter how close it'd be.
       - preferEdge "min" (Fly 112): the mirror image — prefer the length
         closest to the window's lower edge (the skier's actual height),
         reaching up toward the upper edge (+5cm) only if nothing fits.
     Either direction, if literally nothing in the catalogue satisfies that
     hard ceiling/floor (the model simply doesn't come in a short/long
     enough size), the closest size on the wrong side of it is used as a
     last resort, with a note. Separately: if the ideal match turns out to
     be sold out, fall back to the closest length that's actually in stock
     and say so. */
  function pickWindowed(lengths, windowMin, windowMax, preferEdge, tieBreak) {
    function closestTo(list, target) {
      var best = list[0];
      var bestDiff = Math.abs(list[0].cm - target);
      for (var i = 1; i < list.length; i++) {
        var diff = Math.abs(list[i].cm - target);
        if (diff < bestDiff - 1e-9) {
          best = list[i];
          bestDiff = diff;
        } else if (Math.abs(diff - bestDiff) < 1e-9) {
          if (tieBreak === 'longer' && list[i].cm > best.cm) best = list[i];
          if (tieBreak === 'shorter' && list[i].cm < best.cm) best = list[i];
        }
      }
      return best;
    }

    var inWindow = lengths.filter(function (l) { return l.cm >= windowMin && l.cm <= windowMax; });
    if (inWindow.length) {
      return { chosen: closestTo(inWindow, preferEdge === 'max' ? windowMax : windowMin), edgeNote: false };
    }

    if (preferEdge === 'max') {
      var underCeiling = lengths.filter(function (l) { return l.cm <= windowMax; });
      if (underCeiling.length) return { chosen: closestTo(underCeiling, windowMax), edgeNote: false };
      // Nothing short enough exists at all — shortest available is the only option.
      return { chosen: lengths[0], edgeNote: true };
    } else {
      var overFloor = lengths.filter(function (l) { return l.cm >= windowMin; });
      if (overFloor.length) return { chosen: closestTo(overFloor, windowMin), edgeNote: false };
      // Nothing long enough exists at all — longest available is the only option.
      return { chosen: lengths[lengths.length - 1], edgeNote: true };
    }
  }

  function computeLength(ski, product, heightCm) {
    var variants = (product && product.variants) || [];
    var lengths = variants
      .map(function (v) {
        var n = parseFloat(v.title);
        return isNaN(n) ? null : { cm: n, available: !!v.available, variantId: v.id, price: v.price };
      })
      .filter(Boolean)
      .sort(function (a, b) { return a.cm - b.cm; });

    if (!lengths.length) return null;

    var rule = ski.lengthRule;
    var windowMin = heightCm + rule.offsetMinCm;
    var windowMax = heightCm + rule.offsetMaxCm;
    var picked = pickWindowed(lengths, windowMin, windowMax, rule.preferEdge || 'max', rule.tieBreak);
    var recommended = picked.chosen; // the true recommendation — shown as-is even if sold out
    var edgeNote = picked.edgeNote;

    // The recommended length is a recommendation, not necessarily what gets
    // added to cart — the size dropdown next to it is what actually decides
    // that, and it can't be set to a sold-out size. So separately work out
    // which size that dropdown (and Add to Cart) should default to: the
    // recommendation itself if it's in stock, otherwise the closest size
    // that actually is.
    var defaultPick = recommended;
    var soldOutNote = false;
    if (!recommended.available) {
      var availableLengths = lengths.filter(function (l) { return l.available; });
      if (availableLengths.length) {
        var fallback = availableLengths[0];
        var fallbackDiff = Math.abs(fallback.cm - recommended.cm);
        for (var j = 1; j < availableLengths.length; j++) {
          var d = Math.abs(availableLengths[j].cm - recommended.cm);
          if (d < fallbackDiff) {
            fallback = availableLengths[j];
            fallbackDiff = d;
          }
        }
        defaultPick = fallback;
        soldOutNote = true;
      }
    }

    return {
      cm: recommended.cm,
      variantId: recommended.variantId,
      available: recommended.available,
      defaultVariantId: defaultPick.variantId,
      edgeNote: edgeNote,
      soldOutNote: soldOutNote
    };
  }

  /* Tree helpers ------------------------------------------------------------ */

  function maxDepthFrom(nodeId, nodes, memo) {
    if (memo[nodeId] != null) return memo[nodeId];
    var node = nodes[nodeId];
    if (!node) { memo[nodeId] = 0; return 0; } // result node
    var depth = 0;
    node.answers.forEach(function (ans) {
      depth = Math.max(depth, 1 + maxDepthFrom(ans.next, nodes, memo));
    });
    memo[nodeId] = depth;
    return depth;
  }

  /* One instance per modal (there is a single shared modal in the DOM,
     reused by every [data-ski-finder-open] trigger, config permitting). */
  function SkiFinder(root_el) {
    var sectionId = root_el.getAttribute('data-section-id');
    var configEl = document.getElementById('SkiFinderConfig-' + sectionId);
    if (!configEl) return;

    var localConfig;
    try {
      localConfig = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    var modal = document.getElementById('SkiFinderModal');
    if (!modal) return;

    var dialog = modal.querySelector('.ski-finder-modal__dialog');
    var body = modal.querySelector('[data-ski-finder-body]');
    var progressWrap = modal.querySelector('[data-ski-finder-progress]');
    var progressFill = modal.querySelector('[data-ski-finder-progress-fill]');
    var backBtn = modal.querySelector('[data-ski-finder-back]');
    var closeEls = modal.querySelectorAll('[data-ski-finder-close]');
    var restartBtn = modal.querySelector('[data-ski-finder-restart]');
    var titleEl = modal.querySelector('#SkiFinderTitle');

    var data = null; // the full ski-finder-config.json, loaded lazily
    var depthMemo = {};
    var stack = []; // {kind:'question'|'height'|'result', nodeId/resultId, heightCm}
    var lastFocused = null;

    function dataLayerPush(event, detail) {
      if (window.dataLayer) {
        window.dataLayer.push(Object.assign({ event: event }, detail));
      }
    }

    function copy(key) {
      var lang = currentLang();
      var table = (data.copy && (data.copy[lang] || data.copy.en)) || {};
      return table[key] != null ? table[key] : key;
    }

    function openModal() {
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);
      dialog.focus();

      if (!data) {
        loadConfig(localConfig.configUrl).then(function (cfg) {
          data = cfg;
          goToNode(data.tree.root);
        });
      }
    }

    function closeModal() {
      modal.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        closeModal();
        return;
      }
      if (e.key === 'Tab') {
        var focusable = dialog.querySelectorAll(
          'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function restart() {
      stack = [];
      goToNode(data.tree.root);
    }

    function goBack() {
      if (stack.length < 2) return;
      stack.pop();
      renderCurrent();
    }

    function goToNode(nodeId) {
      stack.push({ kind: 'question', nodeId: nodeId });
      renderCurrent();
    }

    function selectAnswer(node, nodeId, answer) {
      dataLayerPush('ski_finder_step', {
        skiFinderStep: nodeId,
        skiFinderAnswer: answer.label
      });

      var nextId = answer.next;
      if (data.tree.nodes[nextId]) {
        stack.push({ kind: 'question', nodeId: nextId });
      } else {
        // nextId is a result id — height always comes right before a result.
        stack.push({ kind: 'height', resultId: nextId });
      }
      renderCurrent();
    }

    function confirmHeight(resultId, heightCm) {
      stack.push({ kind: 'result', resultId: resultId, heightCm: heightCm });
      renderCurrent();
    }

    function currentQuestionDepth() {
      var n = 0;
      stack.forEach(function (s) { if (s.kind === 'question') n++; });
      return n;
    }

    function updateProgress(entry) {
      if (entry.kind === 'result') {
        progressWrap.hidden = true;
        return;
      }
      progressWrap.hidden = false;
      // "answeredBefore" excludes the screen being shown right now (it isn't
      // answered yet) — remainingFromHere is the longest path still possible
      // starting at (and including) the current node, so the two together
      // give a running estimate of the branch's total length that tightens
      // up as deeper, shorter branches get confirmed.
      var answeredBefore = currentQuestionDepth() - (entry.kind === 'question' ? 1 : 0);
      var remainingFromHere = entry.kind === 'question' ? maxDepthFrom(entry.nodeId, data.tree.nodes, depthMemo) : 0;
      var total = answeredBefore + remainingFromHere + 1; // +1 for the height step every branch ends with
      progressFill.style.width = Math.round((answeredBefore / total) * 100) + '%';
    }

    function renderCurrent() {
      var entry = stack[stack.length - 1];
      backBtn.hidden = stack.length < 2;
      restartBtn.hidden = stack.length < 2;
      restartBtn.textContent = copy('restartButton');
      updateProgress(entry);

      if (entry.kind === 'question') renderQuestion(entry);
      else if (entry.kind === 'height') renderHeight(entry);
      else renderResult(entry);

      body.scrollTop = 0;
      var firstFocusable = body.querySelector('button, input, a[href]');
      if (firstFocusable) firstFocusable.focus();
    }

    function renderQuestion(entry) {
      var node = data.tree.nodes[entry.nodeId];
      var html = '<div class="ski-finder-screen ski-finder-question">';
      html += '<h3 class="ski-finder-question__text">' + escapeHtml(localize(node.text)) + '</h3>';
      html += '<div class="ski-finder-answers">';
      node.answers.forEach(function (ans, i) {
        var sub = localize(ans.sub);
        html +=
          '<button type="button" class="ski-finder-answer" data-answer-index="' + i + '">' +
          '<span class="ski-finder-answer__label">' + escapeHtml(localize(ans.label)) + '</span>' +
          (sub ? '<span class="ski-finder-answer__sub">' + escapeHtml(sub) + '</span>' : '') +
          '</button>';
      });
      html += '</div></div>';
      body.innerHTML = html;

      body.querySelectorAll('[data-answer-index]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var ans = node.answers[parseInt(btn.getAttribute('data-answer-index'), 10)];
          selectAnswer(node, entry.nodeId, ans);
        });
      });
    }

    function renderHeight(entry) {
      var hq = data.heightQuestion;
      var value = hq.default;
      var html = '<div class="ski-finder-screen ski-finder-height">';
      html += '<h3 class="ski-finder-question__text">' + escapeHtml(localize(hq.text)) + '</h3>';
      html +=
        '<div class="ski-finder-height__value">' +
        '<span id="SkiFinderHeightValue">' + value + '</span> <span>' + escapeHtml(copy('heightUnit')) + '</span>' +
        '</div>';
      html +=
        '<input type="range" min="' + hq.min + '" max="' + hq.max + '" value="' + value + '" id="SkiFinderHeightInput">';
      html +=
        '<div class="ski-finder-height__continue">' +
        '<button type="button" class="button" id="SkiFinderHeightContinue">' + escapeHtml(copy('heightContinue')) + '</button>' +
        '</div></div>';
      body.innerHTML = html;

      var continueBtn = document.getElementById('SkiFinderHeightContinue');
      var input = document.getElementById('SkiFinderHeightInput');
      var valueEl = document.getElementById('SkiFinderHeightValue');
      input.addEventListener('input', function () {
        valueEl.textContent = input.value;
      });

      continueBtn.addEventListener('click', function () {
        confirmHeight(entry.resultId, parseInt(input.value, 10));
      });
    }

    function renderResult(entry) {
      var result = data.results[entry.resultId];
      var mainSkiId = result.main;
      var mainSki = data.skis[mainSkiId];

      body.innerHTML = '<div class="ski-finder-screen ski-finder-result"><p class="ski-finder-empty">Loading…</p></div>';

      fetchProduct(mainSki.handle).then(function (product) {
        if (!product && result.similar.length) {
          // Missing product (e.g. not live yet) — promote the first similar
          // ski to the main recommendation instead, with no visible error.
          var fallbackId = result.similar[0];
          var fallbackSki = data.skis[fallbackId];
          return fetchProduct(fallbackSki.handle).then(function (fallbackProduct) {
            renderResultContent(entry, fallbackId, fallbackSki, fallbackProduct, result.similar.slice(1));
          });
        }
        renderResultContent(entry, mainSkiId, mainSki, product, result.similar);
      });
    }

    function renderBars(ski) {
      var bars = [
        ['uphillLabel', ski.profile.uphill, ski.showUphill],
        ['downhillLabel', ski.profile.downhill, true],
        ['groomedLabel', ski.profile.groomed, true],
        ['powderLabel', ski.profile.powder, true],
        ['playfulLabel', ski.profile.playful, true]
      ];
      var html = '<div class="ski-finder-bars">';
      bars.forEach(function (b) {
        if (!b[2]) return;
        html += '<div class="ski-finder-bar"><span>' + escapeHtml(copy(b[0])) + '</span><span class="ski-finder-bar__track">';
        for (var i = 1; i <= 5; i++) {
          html += '<span class="ski-finder-bar__segment' + (i <= b[1] ? ' is-filled' : '') + '"></span>';
        }
        html += '</span></div>';
      });
      html += '</div>';
      return html;
    }

    function renderResultContent(entry, skiId, ski, product, similarIds) {
      var result = data.results[entry.resultId];
      var image = product && product.featured_image;
      var length = product ? computeLength(ski, product, entry.heightCm) : null;

      dataLayerPush('ski_finder_result', {
        skiFinderPath: stack.filter(function (s) { return s.kind === 'question'; }).map(function (s) { return s.nodeId; }),
        skiFinderResult: skiId,
        skiFinderLength: length ? length.cm : null
      });

      var html = '<div class="ski-finder-screen ski-finder-result">';
      html += '<p class="ski-finder-result__category">' + escapeHtml(localize(result.categoryPath)) + '</p>';
      html += '<div class="ski-finder-result__main">';
      html += '<div class="ski-finder-result__image">' + (image ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(ski.name) + '">' : '') + '</div>';
      html += '<div>';
      html += '<h3 class="ski-finder-result__name">' + escapeHtml(ski.name) + '</h3>';
      html += '<p class="ski-finder-result__meta">' + ski.waist + escapeHtml(copy('waistSuffix')) + '</p>';
      if (product) html += '<p class="ski-finder-result__price">' + formatMoney(product.price) + '</p>';
      html += renderBars(ski);
      html += '<p class="ski-finder-result__why">' + escapeHtml(localize(result.why)) + '</p>';

      if (length) {
        html += '<div class="ski-finder-length">';
        html += '<span class="ski-finder-length__label">' + escapeHtml(copy('recommendedLengthLabel')) + '</span>';
        html += '<span class="ski-finder-length__value">' + length.cm + ' ' + escapeHtml(copy('heightUnit')) + '</span>';
        if (length.edgeNote) html += '<span class="ski-finder-length__note">' + escapeHtml(copy('lengthEdgeNote')) + '</span>';
        if (length.soldOutNote) html += '<span class="ski-finder-length__note">' + escapeHtml(copy('lengthSoldOutNote')) + '</span>';
        html += '</div>';

        // The recommended length above is shown as-is even when sold out —
        // it's a recommendation, not an add-to-cart target. This dropdown
        // (defaulting to that recommendation, or the closest in-stock size
        // if the recommendation itself isn't available) is the real,
        // purchasable choice: sold-out sizes are listed but disabled.
        html += '<div class="ski-finder-size-picker">';
        html += '<label class="ski-finder-size-picker__label" for="SkiFinderSizeSelect">' + escapeHtml(copy('sizeLabel')) + '</label>';
        html += '<select id="SkiFinderSizeSelect" class="ski-finder-size-picker__select">';
        (product.variants || []).forEach(function (v) {
          var selected = v.id === length.defaultVariantId ? ' selected' : '';
          var disabled = v.available ? '' : ' disabled';
          var suffix = v.available ? '' : ' (' + copy('soldOutOption') + ')';
          html += '<option value="' + v.id + '"' + selected + disabled + '>' + escapeHtml(v.title) + ' ' + escapeHtml(copy('heightUnit')) + suffix + '</option>';
        });
        html += '</select>';
        html += '</div>';
      }

      html += '<div class="ski-finder-result__actions">';
      if (product) {
        html += '<a class="button button--secondary" href="' + root + 'products/' + ski.handle + (length ? '?variant=' + length.defaultVariantId : '') + '" id="SkiFinderViewSki">' + escapeHtml(copy('viewSkiButton')) + '</a>';
        if (length) {
          html += '<button type="button" class="button" id="SkiFinderAddToCart" data-variant-id="' + length.defaultVariantId + '">' + escapeHtml(copy('addToCartButton')) + '</button>';
        }
      }
      html += '</div>';
      html +=
        '<a class="ski-finder-result__shopfinder" href="' + root.slice(0, -1) + copy('findShopUrl') + '">' +
        escapeHtml(copy('findShopButton')) + '</a>';
      html += '</div></div>';

      var validSimilar = similarIds.filter(function (id) { return id !== skiId; });
      html += '<div data-ski-finder-similar-slot></div>';
      html += '<div class="ski-finder-modal__footer-spacer"></div>';
      html += '</div>';

      body.innerHTML = html;

      var sizeSelect = document.getElementById('SkiFinderSizeSelect');
      var viewSkiLink = document.getElementById('SkiFinderViewSki');
      var addToCartBtn = document.getElementById('SkiFinderAddToCart');

      if (sizeSelect) {
        sizeSelect.addEventListener('change', function () {
          if (addToCartBtn) addToCartBtn.setAttribute('data-variant-id', sizeSelect.value);
          if (viewSkiLink) viewSkiLink.href = root + 'products/' + ski.handle + '?variant=' + sizeSelect.value;
        });
      }

      if (addToCartBtn) {
        addToCartBtn.addEventListener('click', function () {
          addToCartBtn.disabled = true;
          fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(addToCartBtn.getAttribute('data-variant-id'), 10), quantity: 1 }] })
          })
            .then(function () {
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              document.documentElement.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
              addToCartBtn.textContent = copy('addToCartButton');
              addToCartBtn.disabled = false;
            })
            .catch(function () {
              addToCartBtn.disabled = false;
            });
        });
      }

      if (validSimilar.length) {
        Promise.all(
          validSimilar.map(function (id) {
            var s = data.skis[id];
            return fetchProduct(s.handle).then(function (p) { return { id: id, ski: s, product: p }; });
          })
        ).then(function (items) {
          var usable = items.filter(function (it) { return it.product; });
          if (!usable.length) return;
          var slot = body.querySelector('[data-ski-finder-similar-slot]');
          if (!slot) return;
          var sHtml = '<h4 class="ski-finder-similar-heading">' + escapeHtml(copy('similarHeading')) + '</h4>';
          sHtml += '<div class="ski-finder-similar-list">';
          usable.forEach(function (it) {
            var sLength = computeLength(it.ski, it.product, entry.heightCm);
            var sImage = it.product.featured_image;
            sHtml +=
              '<a class="ski-finder-similar-card" href="' + root + 'products/' + it.ski.handle + '">' +
              '<div class="ski-finder-similar-card__image">' + (sImage ? '<img src="' + escapeHtml(sImage) + '" alt="' + escapeHtml(it.ski.name) + '">' : '') + '</div>' +
              '<p class="ski-finder-similar-card__name">' + escapeHtml(it.ski.name) + '</p>' +
              '<p class="ski-finder-similar-card__meta">' + formatMoney(it.product.price) +
              (sLength ? ' · ' + sLength.cm + escapeHtml(copy('heightUnit')) : '') + '</p>' +
              '</a>';
          });
          sHtml += '</div>';
          slot.outerHTML = sHtml;
        });
      }
    }

    titleEl.textContent = localConfig.modalTitle;
    document.querySelectorAll('[data-ski-finder-open]').forEach(function (btn) {
      btn.addEventListener('click', openModal);
    });
    // Any link ending in #ski-finder also opens it — lets an existing button
    // elsewhere (e.g. a second hero banner button, via its own link field)
    // trigger the modal without that shared section needing to know
    // anything about Ski Finder.
    document.querySelectorAll('a[href$="#ski-finder"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        openModal();
      });
    });
    backBtn.addEventListener('click', goBack);
    restartBtn.addEventListener('click', restart);
    closeEls.forEach(function (el) { el.addEventListener('click', closeModal); });
  }

  /* Debug mode --------------------------------------------------------------
     ?skifinder=debug walks every path of the tree, checks every handle
     resolves, and checks every ski only shows up where the brand allows it. */
  function runDebugMode(root_el) {
    var sectionId = root_el.getAttribute('data-section-id');
    var configEl = document.getElementById('SkiFinderConfig-' + sectionId);
    if (!configEl) return;
    var localConfig = JSON.parse(configEl.textContent);

    loadConfig(localConfig.configUrl).then(function (data) {
      var lines = [];
      var errors = 0;

      function pathAllowed(skiId, categoryPath) {
        var allowedList = data.allowedPlacements[skiId] || [];
        return allowedList.some(function (a) {
          return categoryPath === a || categoryPath.indexOf(a + ' ›') === 0;
        });
      }

      function walk(nodeId, pathLabels) {
        var node = data.tree.nodes[nodeId];
        if (node) {
          node.answers.forEach(function (ans) {
            walk(ans.next, pathLabels.concat(localize(ans.label)));
          });
          return;
        }
        var result = data.results[nodeId];
        if (!result) {
          lines.push('ERROR: dangling pointer "' + nodeId + '" at path ' + pathLabels.join(' > '));
          errors++;
          return;
        }
        var mainSki = data.skis[result.main];
        var checks = [];
        // categoryKey is the stable English identifier allowedPlacements is
        // written against — categoryPath is now a {en, fr, de} display
        // string and isn't what placement rules are checked against.

        if (!mainSki) {
          lines.push('ERROR: result "' + nodeId + '" has unknown main ski "' + result.main + '"');
          errors++;
        } else if (!pathAllowed(result.main, result.categoryKey)) {
          lines.push('ERROR: "' + mainSki.name + '" not allowed in "' + result.categoryKey + '" (path: ' + pathLabels.join(' > ') + ')');
          errors++;
        }

        result.similar.forEach(function (sid) {
          var s = data.skis[sid];
          if (!s) {
            lines.push('ERROR: result "' + nodeId + '" has unknown similar ski "' + sid + '"');
            errors++;
          } else if (!pathAllowed(sid, result.categoryKey)) {
            lines.push('ERROR: similar ski "' + s.name + '" not allowed in "' + result.categoryKey + '" (path: ' + pathLabels.join(' > ') + ')');
            errors++;
          }
        });

        checks.push(
          fetchProduct(mainSki ? mainSki.handle : '').then(function (p) {
            var status = p ? 'OK' : 'MISSING (fallback applies)';
            lines.push(
              '[' + nodeId + '] ' + pathLabels.join(' > ') + '  →  ' + (mainSki ? mainSki.name : '?') +
              ' (' + status + ')  similar: ' + result.similar.map(function (sid) { return (data.skis[sid] || {}).name || sid; }).join(', ')
            );
          })
        );

        Promise.all(checks).then(renderReportIfDone);
      }

      var totalLeaves = 0;
      var doneLeaves = 0;
      function countLeaves(nodeId) {
        var node = data.tree.nodes[nodeId];
        if (!node) { totalLeaves++; return; }
        node.answers.forEach(function (a) { countLeaves(a.next); });
      }
      countLeaves(data.tree.root);

      function renderReportIfDone() {
        doneLeaves++;
        if (doneLeaves >= totalLeaves) {
          document.body.innerHTML =
            '<pre style="font:1.3rem/1.5 monospace;padding:3rem;white-space:pre-wrap;">' +
            'Ski Finder debug report — ' + totalLeaves + ' path(s), ' + errors + ' error(s)\n\n' +
            lines.sort().join('\n') +
            '</pre>';
        }
      }

      walk(data.tree.root, []);
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-ski-finder-root]');
    if (!roots.length) return;

    var params = new URLSearchParams(window.location.search);
    if (params.get('skifinder') === 'debug') {
      runDebugMode(roots[0]);
      return;
    }

    roots.forEach(function (r) { SkiFinder(r); });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
